(() => {
  const root = document.querySelector('[data-bbc-baby-soother]');
  if (!root) return;

  const dbName = 'bbc-baby-soother-v1';
  const storeName = 'audio';
  const player = root.querySelector('[data-audio-player]');
  const phone = root.querySelector('.baby-soother__phone');
  const menuPanel = root.querySelector('[data-soother-menu-panel]');
  const menuButton = root.querySelector('[data-soother-menu]');
  const cards = [...root.querySelectorAll('[data-sound-card]')];
  const offlineStatus = root.querySelector('[data-offline-status]');
  const homeView = root.querySelector('[data-home-view]');
  const categoryViews = [...root.querySelectorAll('[data-category-view]')];
  let activeKey = '';
  let activeButton = null;
  let activeObjectUrl = '';
  let recorder = null;
  let recorderStream = null;
  let recorderChunks = [];
  let recordingBlob = null;
  let recordingUrl = '';
  let recordingTimer = null;
  let recordingStartedAt = 0;
  let recordingActive = false;

  menuButton?.addEventListener('click', () => {
    const opening = menuPanel.hidden;
    menuPanel.hidden = !opening;
    menuButton.setAttribute('aria-expanded', String(opening));
  });
  menuPanel?.querySelectorAll('a').forEach(link => link.addEventListener('click', () => {
    menuPanel.hidden = true;
    menuButton?.setAttribute('aria-expanded', 'false');
  }));

  const openDatabase = () => new Promise((resolve, reject) => {
    if (!('indexedDB' in window)) return reject(new Error('Offline storage is not available in this browser.'));
    const request = indexedDB.open(dbName, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(storeName)) request.result.createObjectStore(storeName);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Could not open local audio storage.'));
  });
  const getStored = async key => {
    const db = await openDatabase();
    return new Promise((resolve, reject) => {
      const request = db.transaction(storeName, 'readonly').objectStore(storeName).get(key);
      request.onsuccess = () => { db.close(); resolve(request.result || null); };
      request.onerror = () => { db.close(); reject(request.error); };
    });
  };
  const saveStored = async (key, blob) => {
    const db = await openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readwrite');
      transaction.objectStore(storeName).put(blob, key);
      transaction.oncomplete = () => { db.close(); resolve(); };
      transaction.onerror = () => { db.close(); reject(transaction.error || new Error('Could not save the sound.')); };
    });
  };

  function setStatus(card, message) {
    const status = card?.querySelector('[data-track-status]');
    if (status) status.textContent = message;
  }
  function showCategory(category, updateHistory = true) {
    const view = categoryViews.find(item => item.dataset.categoryView === category);
    if (!view) return showHome(false);
    stopPlayer();
    homeView.hidden = true;
    phone?.classList.add('is-detail-open');
    categoryViews.forEach(item => { item.hidden = item !== view; });
    if (updateHistory) history.pushState({ babySootherCategory: category }, '', '#sounds-' + category);
    view.querySelector('[data-back-home]')?.focus({ preventScroll: true });
    view.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  function showHome(updateHistory = true) {
    stopPlayer();
    categoryViews.forEach(item => { item.hidden = true; });
    homeView.hidden = false;
    phone?.classList.remove('is-detail-open');
    if (updateHistory && location.hash.startsWith('#sounds-')) history.pushState({}, '', location.pathname + location.search);
    homeView.querySelector('[data-open-category]')?.focus({ preventScroll: true });
    homeView.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  root.querySelectorAll('[data-open-category]').forEach(button => button.addEventListener('click', () => showCategory(button.dataset.openCategory)));
  root.querySelectorAll('[data-back-home]').forEach(button => button.addEventListener('click', () => showHome()));
  window.addEventListener('popstate', () => {
    const category = location.hash.match(/^#sounds-(.+)$/)?.[1];
    if (category) showCategory(category, false);
    else showHome(false);
  });
  const initialCategory = location.hash.match(/^#sounds-(.+)$/)?.[1];
  if (initialCategory) showCategory(initialCategory, false);
  function releaseActiveUrl() {
    if (activeObjectUrl) URL.revokeObjectURL(activeObjectUrl);
    activeObjectUrl = '';
  }
  function stopPlayer() {
    player.pause();
    player.removeAttribute('src');
    player.load();
    if (activeButton) {
      setStatus(activeButton.closest('[data-sound-card]'), 'Playback stopped.');
      activeButton.setAttribute('aria-pressed', 'false');
      activeButton.setAttribute('aria-label', 'Play sound');
      const label = activeButton.querySelector('span');
      if (label) label.textContent = '▶';
      else activeButton.textContent = '▶';
    }
    activeButton = null;
    activeKey = '';
    releaseActiveUrl();
  }
  async function sourceFor(key, fallback) {
    const blob = await getStored(key);
    if (!blob) return fallback;
    releaseActiveUrl();
    activeObjectUrl = URL.createObjectURL(blob);
    return activeObjectUrl;
  }
  async function toggleTrack(card) {
    const key = card.dataset.track;
    const button = card.querySelector('[data-play]');
    const src = card.dataset.audio;
    if (activeKey === key && !player.paused) { stopPlayer(); setStatus(card, 'Playback paused.'); return; }
    stopPlayer();
    button.setAttribute('aria-pressed', 'true');
    const title = card.querySelector('h2, h3, strong')?.textContent?.trim() || 'sound';
    button.setAttribute('aria-label', 'Pause ' + title);
    const label = button.querySelector('span');
    if (label) label.textContent = 'Ⅱ';
    activeButton = button;
    activeKey = key;
    setStatus(card, 'Loading sound…');
    try {
      player.src = await sourceFor(key, src);
      await player.play();
      offlineStatus.hidden = false;
      setStatus(card, 'Playing on repeat. Tap Pause to stop.');
    } catch (error) {
      button.setAttribute('aria-pressed', 'false');
      activeButton = null;
      activeKey = '';
      setStatus(card, navigator.onLine ? 'This sound could not start. Please try again.' : 'Connect once to download this sound for offline listening.');
    }
  }
  async function downloadTrack(card) {
    const button = card.querySelector('[data-download]');
    const key = card.dataset.track;
    button.disabled = true;
    setStatus(card, 'Saving for offline listening…');
    try {
      let blob = await getStored(key);
      if (!blob) {
        const response = await fetch(card.dataset.audio, { cache: 'no-cache' });
        if (!response.ok) throw new Error('Download failed.');
        blob = await response.blob();
        if (!blob.type.startsWith('audio/')) throw new Error('The audio file was not available.');
        await saveStored(key, blob);
      }
      const href = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = href;
      link.download = key + '.wav';
      document.body.append(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(href), 1500);
      setStatus(card, 'Saved on this device and ready for offline listening.');
      offlineStatus.hidden = false;
      offlineStatus.textContent = 'Downloaded sounds stay in this browser on this device. The Baby Soother page itself still needs an internet connection to reopen.';
    } catch (error) {
      setStatus(card, error.message || 'Could not save this sound.');
    } finally { button.disabled = false; }
  }

  cards.forEach(card => {
    card.querySelector('[data-play]').addEventListener('click', () => toggleTrack(card));
    card.querySelector('[data-download]').addEventListener('click', () => downloadTrack(card));
  });
  player.addEventListener('ended', () => { if (!player.loop) stopPlayer(); });
  player.addEventListener('error', () => { if (activeButton) activeButton.setAttribute('aria-pressed', 'false'); activeButton = null; activeKey = ''; });

  const recordButton = root.querySelector('[data-record]');
  const recordPlay = root.querySelector('[data-record-play]');
  const recordDownload = root.querySelector('[data-record-download]');
  const timerLabel = root.querySelector('[data-record-timer]');
  const recordStatus = root.querySelector('[data-record-status]');
  function elapsedLabel(seconds) { return `0:${String(Math.max(0, 60 - seconds)).padStart(2, '0')} left`; }
  function finishRecording() {
    if (!recorder || recorder.state === 'inactive') return;
    recorder.stop();
    recordingActive = false;
    recordButton.textContent = 'Record again ●';
    clearInterval(recordingTimer);
    recorderStream?.getTracks().forEach(track => track.stop());
    recorderStream = null;
    if (timerLabel) timerLabel.textContent = 'Up to 1:00';
  }
  recordButton.addEventListener('click', async () => {
    if (recordingActive) { finishRecording(); return; }
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      recordStatus.textContent = 'Recording is not supported in this browser. Try a current version of Chrome or Safari.';
      return;
    }
    try {
      recorderStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recorderChunks = [];
      recorder = new MediaRecorder(recorderStream);
      recorder.ondataavailable = event => { if (event.data.size) recorderChunks.push(event.data); };
      recorder.onstop = async () => {
        recordingBlob = new Blob(recorderChunks, { type: recorder.mimeType || 'audio/webm' });
        if (recordingUrl) URL.revokeObjectURL(recordingUrl);
        recordingUrl = URL.createObjectURL(recordingBlob);
        recordPlay.disabled = false;
        recordDownload.disabled = false;
        recordStatus.textContent = 'Recording ready. It is kept on this device only.';
        try { await saveStored('parent-hum', recordingBlob); } catch { recordStatus.textContent = 'Recording ready; local offline storage is unavailable in this browser.'; }
      };
      recorder.start();
      recordingActive = true;
      recordingStartedAt = Date.now();
      recordButton.textContent = 'Stop recording ■';
      recordStatus.textContent = 'Recording. Hum or sing softly; stop any time.';
      recordingTimer = setInterval(() => {
        const seconds = Math.min(60, Math.floor((Date.now() - recordingStartedAt) / 1000));
        if (timerLabel) timerLabel.textContent = elapsedLabel(seconds);
        if (seconds >= 60) finishRecording();
      }, 250);
    } catch (error) {
      recordStatus.textContent = error.name === 'NotAllowedError' ? 'Allow microphone access in your browser to record a hum.' : 'Could not start recording. Please check microphone access and try again.';
      recorderStream?.getTracks().forEach(track => track.stop());
      recorderStream = null;
    }
  });
  recordPlay.addEventListener('click', async () => {
    if (activeKey === 'parent-hum' && !player.paused) {
      stopPlayer();
      recordStatus.textContent = 'Recording playback paused.';
      return;
    }
    stopPlayer();
    activeKey = 'parent-hum';
    try {
      const blob = await getStored('parent-hum');
      player.src = blob ? (activeObjectUrl = URL.createObjectURL(blob)) : recordingUrl;
      player.loop = true;
      await player.play();
      offlineStatus.hidden = false;
      offlineStatus.textContent = 'Your recorded hum is saved on this device and playing on repeat.';
      recordStatus.textContent = 'Your hum is playing on repeat. Tap Play again to stop.';
    } catch { recordStatus.textContent = 'Could not play this recording.'; }
  });
  recordDownload.addEventListener('click', () => {
    if (!recordingBlob) return;
    const href = URL.createObjectURL(recordingBlob);
    const link = document.createElement('a');
    link.href = href;
    link.download = 'my-baby-soother-hum.' + (recordingBlob.type.includes('mp4') ? 'm4a' : 'webm');
    document.body.append(link); link.click(); link.remove();
    setTimeout(() => URL.revokeObjectURL(href), 1500);
  });
  window.addEventListener('pagehide', () => {
    clearInterval(recordingTimer);
    stopPlayer();
    recorderStream?.getTracks().forEach(track => track.stop());
    if (recordingUrl) URL.revokeObjectURL(recordingUrl);
  });
  document.addEventListener('shopify:section:unload', event => {
    if (root.contains(event.target)) stopPlayer();
  });

  getStored('parent-hum').then(blob => {
    if (!blob) return;
    recordingBlob = blob;
    recordPlay.disabled = false;
    recordDownload.disabled = false;
    recordStatus.textContent = 'Your saved hum is ready on this device.';
  }).catch(() => {});
  cards.forEach(card => getStored(card.dataset.track).then(blob => {
    if (blob) setStatus(card, 'Available offline on this device.');
  }).catch(() => {}));
})();
